/**
 * Column Responsibilities Component
 * Shows data stewards, owners, and responsible contacts
 */
import React from 'react';
import { Box, Typography, Paper, Avatar, Chip, Button } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';

interface ColumnResponsibilitiesProps {
  columnName: string;
  domain: string;
}

const ColumnResponsibilities: React.FC<ColumnResponsibilitiesProps> = ({ columnName, domain }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Responsibilities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Data stewards, owners, and responsible contacts for this column.
      </Typography>

      {/* Data Owner */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#003366' }}>
          Data Owner
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#003366' }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Sarah Johnson
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Director of Data Management
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="body2">sarah.johnson@e-unify.ai</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="body2">+1 (555) 123-4567</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="body2">Data Management Department</Typography>
              </Box>
            </Box>
            <Button variant="outlined" size="small" startIcon={<EmailIcon />}>
              Contact Owner
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Responsibilities:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            • Final approval authority for data changes<br />
            • Accountable for data quality and compliance<br />
            • Defines data policies and standards
          </Typography>
        </Box>
      </Paper>

      {/* Data Stewards */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#003366' }}>
          Data Stewards
        </Typography>
        
        {/* Primary Steward */}
        <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip label="PRIMARY" size="small" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: '#1976d2' }}>
              MR
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Michael Rodriguez
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Senior Data Analyst - {domain} Domain
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption">michael.rodriguez@e-unify.ai</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                "Responsible for day-to-day data quality monitoring and issue resolution"
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Secondary Steward */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip label="SECONDARY" size="small" sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: '#757575' }}>
              JC
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Jennifer Chen
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Data Quality Specialist
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption">jennifer.chen@e-unify.ai</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                "Backup steward for validation rule management and quality reporting"
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Technical Contact */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#003366' }}>
          Technical Contact
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: '#388e3c' }}>
            DK
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              David Kim
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Database Administrator
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
              <Typography variant="caption">david.kim@e-unify.ai</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 14, color: '#666' }} />
              <Typography variant="caption">+1 (555) 987-6543</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f8e9', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Technical Support:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            • Database schema changes and optimization<br />
            • ETL pipeline troubleshooting<br />
            • Performance tuning and indexing
          </Typography>
        </Box>
      </Paper>

      {/* Governance Team */}
      <Paper sx={{ p: 3, bgcolor: '#e3f2fd' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Data Governance Team
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For policy questions, compliance issues, or escalations:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            <Typography variant="body2">data-governance@e-unify.ai</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            <Typography variant="body2">Data Governance Office</Typography>
          </Box>
        </Box>
        <Button 
          variant="contained" 
          size="small" 
          sx={{ mt: 2, bgcolor: '#003366' }}
          startIcon={<EmailIcon />}
        >
          Contact Governance Team
        </Button>
      </Paper>
    </Box>
  );
};

export default ColumnResponsibilities;
